import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { FilePenLine } from 'lucide-react';
import { capitalize } from 'lodash';
import { useMutation } from '@apollo/client/react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTrigger,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { AgreementApproveDocument } from 'src/graphql/actions/agreement/agreementApprove.mutation';
import { getApiError, ClientError } from 'src/utils/errors';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const ApproveSignatureTooltip = ({ text }: { text: string }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <Button variant="secondary" disabled className="w-full" size="sm">
            <FilePenLine /> Submit for Signature
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className="bg-popover text-popover-foreground max-w-[320px] border border-border text-sm"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ApproveSignatureDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const {
    softwareEscrow: { id: escrowId, agreement, currentCompanyRole },
    refetch,
  } = useSoftwareEscrow();
  const [approveAgreement, { loading }] = useMutation(AgreementApproveDocument);

  const onApproveSignature = async () => {
    try {
      const { data } = await approveAgreement({
        variables: {
          escrowId,
        },
      });

      if (data?.agreementApprove?.success) {
        setIsOpen(false);
        setTimeout(() => setIsSuccessOpen(true), 200);
      } else {
        throw new ClientError(data?.agreementApprove?.errors?.[0]);
      }
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  const onSuccessOpenChange = (open: boolean) => {
    if (!open) {
      refetch();
      setIsSuccessOpen(false);
    }
  };

  const hasApproved = agreement[`approvedBy${capitalize(currentCompanyRole!)}`];
  if (hasApproved)
    return (
      <ApproveSignatureTooltip text="Your organization has reviewed the agreement and approved it for signature. Waiting for the other party to do the same." />
    );

  if (!agreement?.policy.approve)
    return (
      <ApproveSignatureTooltip
        text="This escrow has pending change requests. 
We'll notify you once they're reviewed so you can submit the agreement for signature."
      />
    );

  return (
    <Fragment>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogTrigger asChild>
          <Button variant="secondary" size="sm">
            <FilePenLine /> Submit for Signature
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to approve this agreement for signature?</AlertDialogTitle>
            <AlertDialogDescription>
              By approving, you confirm that you’ve read and agreed to the terms of this escrow agreement. Once all
              parties have approved, the document will be sent for signature.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="outline">Back</Button>
            </AlertDialogCancel>
            <Button onClick={onApproveSignature} isLoading={loading}>
              Approve for signature
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isSuccessOpen} onOpenChange={onSuccessOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="text-center">
            <DialogTitle className="text-lg">Your approval has been confirmed!</DialogTitle>
            <DialogDescription>
              You’ve approved this version of the agreement. Once all other parties have confirmed, we’ll send it for
              signature.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to escrow</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default ApproveSignatureDialog;
